import { ThemePaletteContext } from '@/components/ThemeWrapper';
import { useTagReplace } from '@/hooks/use-tag-replace';
import { useContext, useId } from 'react';

interface TextBlockProps {
  text: string;
  backgroundColor?: string;
  justifyContent?: string;
  alignItems?: string;
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  rotation?: number;
  inverted?: boolean;
}

function isTransparent(value?: string) {
  if (!value) return true;

  const normalized = value.trim().toLowerCase();
  return normalized === 'transparent' || normalized === 'rgba(0, 0, 0, 0)' || normalized === 'rgba(0,0,0,0)';
}

function getInlineTextColor(text: string) {
  const match = text.match(/color\s*:\s*([^;"']+)/i);
  return match?.[1]?.trim();
}

type TextPalette = {
  pageForeground: string;
  paragraphForeground: string;
  headingForegrounds: {
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    h5: string;
    h6: string;
  };
};

function getThemeTextColor(text: string, palette: TextPalette) {
  const normalizedText = text.toLowerCase();

  if (normalizedText.includes('<h1')) return palette.headingForegrounds.h1;
  if (normalizedText.includes('<h2')) return palette.headingForegrounds.h2;
  if (normalizedText.includes('<h3')) return palette.headingForegrounds.h3;
  if (normalizedText.includes('<h4')) return palette.headingForegrounds.h4;
  if (normalizedText.includes('<h5')) return palette.headingForegrounds.h5;
  if (normalizedText.includes('<h6')) return palette.headingForegrounds.h6;
  if (normalizedText.includes('<p')) return palette.paragraphForeground;

  return palette.pageForeground;
}

function TextBlock({ text = '', backgroundColor, justifyContent = 'flex-start', alignItems = 'flex-start', paddingTop = 0, paddingRight = 0, paddingBottom = 0, paddingLeft = 0, inverted = false }: TextBlockProps) {
  const replacedText = useTagReplace(text);
  const palette = useContext(ThemePaletteContext);
  const rawId = useId();
  const scopeId = 'inv-' + rawId.replace(/[^a-zA-Z0-9]/g, '');
  const originalBackground = isTransparent(backgroundColor) ? palette.cardBackground : backgroundColor!;
  const originalTextColor = getInlineTextColor(replacedText) ?? getThemeTextColor(replacedText, palette);

  return (
    <>
      {inverted && (
        <style>
          {`
            #${scopeId}, #${scopeId} * {
              color: ${originalBackground} !important;
              fill: ${originalBackground} !important;
              -webkit-text-fill-color: ${originalBackground} !important;
            }
            #${scopeId} {
              background-color: ${originalTextColor} !important;
            }
          `}
        </style>
      )}
      <div
        id={scopeId}
        className="max-w-full max-h-full  h-full w-full flex text-block overflow-hidden [&>*]:break-words"
        style={{
          backgroundColor: inverted ? originalTextColor : backgroundColor,
          justifyContent,
          alignItems,
          paddingTop,
          paddingRight,
          paddingBottom,
          paddingLeft,
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: replacedText }} />
      </div>
    </>
  );
}

export default TextBlock;
